// src/services/DeleteMyDataService.js
//
// "Delete All My Data", for real.
//
// What the button used to do: remove localStorage keys starting `vibe_` or
// `friendships_` and say "All data cleared. Fresh start!". The toolkit entries
// (grief entries, unsent letters, pain logs, named friends) live under
// `fv:tool:*` and stayed on the device; the copy synced to the auth hub
// stayed too, and the next visit restored all of it from there.
//
// What it does now, in this order:
//   1. The copy on the auth hub (ToolSyncService.clearHubCopy). If that can't
//      be reached, STOP: wiping the device alone would only let the next
//      visit restore everything from the hub, which is the bug this replaces.
//   2. What the Coach keeps for this app, on the Knowledge Hub (app-scoped:
//      other Dive Deep Dialogues apps' coaching data is not touched).
//   3. Progress and saved insights this app keeps in its own database
//      (users/{uid}/gamification/progress, and the older gamification/{uid}).
//   4. Everything on this device (utils/localData.js: every key the app
//      writes, enumerated from the code).
//
// What it does NOT do, and the page says so: delete the account itself
// (by email for now), or your answers in a Friend Circle (leaving the circle
// deletes them).
//
// Australian English throughout.

import { clearAllLocalData } from '../utils/localData';

async function defaultClearHubCopy() {
  const { clearHubCopy } = await import('./ToolSyncService');
  return clearHubCopy();
}

async function defaultDeleteCoachData() {
  const { auth } = await import('./config/firebase');
  if (!auth?.currentUser) return 'none';
  const { default: coachingService } = await import('./CoachingService');
  const res = await coachingService.deleteAll();
  return res?.success ? 'cleared' : 'failed';
}

async function defaultDeleteOwnProjectData() {
  const { auth, db } = await import('./config/firebase');
  const uid = auth?.currentUser?.uid;
  if (!uid || !db) return 'none';
  try {
    const { doc, deleteDoc } = await import('firebase/firestore');
    await Promise.all([
      deleteDoc(doc(db, 'users', uid, 'gamification', 'progress')),
      deleteDoc(doc(db, 'gamification', uid)),
    ]);
    return 'cleared';
  } catch (error) {
    console.warn('[DeleteMyData] could not delete progress from the database:', error?.message);
    return 'failed';
  }
}

const settle = async (fn) => {
  try {
    return await fn();
  } catch {
    return 'failed';
  }
};

/**
 * Delete this person's Friendships Vibe data from the hub, the Coach, this
 * app's database and this device.
 *
 * Returns { ok, stoppedEarly, report } where report is
 *   { hub, coach, progress: 'cleared' | 'none' | 'failed' | null,
 *     device: { removed, remaining } | null }
 * `ok` is true only when nothing failed and nothing of ours is left on the
 * device. The caller must say what failed rather than claim a clean sweep.
 */
export async function deleteAllMyData({
  clearHubCopy = defaultClearHubCopy,
  deleteCoachData = defaultDeleteCoachData,
  deleteOwnProjectData = defaultDeleteOwnProjectData,
  clearLocal = clearAllLocalData,
} = {}) {
  const report = { hub: null, coach: null, progress: null, device: null };

  report.hub = await settle(clearHubCopy);
  if (report.hub === 'failed') {
    return { ok: false, stoppedEarly: true, report };
  }

  report.coach = await settle(deleteCoachData);
  report.progress = await settle(deleteOwnProjectData);

  const { removed, remaining } = clearLocal();
  report.device = { removed: removed.length, remaining };

  const ok = [report.hub, report.coach, report.progress].every((r) => r !== 'failed') && remaining.length === 0;
  return { ok, stoppedEarly: false, report };
}

export default { deleteAllMyData };
